<template>
  <div>
    <filters-section :includeUnits="false" :showBasicInfectionSiteSelectAll="true" :filterDisplayKeys="getFilterKeys()" :dateRangeStart="global.defaultDateStart" :moreFilters="global.additionalFilters" :defaultValues="global.filterDefaults" v-on:change="formatFilters"> </filters-section>

    <page-section title="Graph" v-loading="graphSection.loading">
      <template slot="title">
        <span class="label do-not-print">Group by</span>
        <el-radio-group class="do-not-print" v-model="graphSection.groupBy" size="small" @change="getSummary">
          <el-radio-button label="Medication"></el-radio-button>
          <el-radio-button label="Infection Site"></el-radio-button>
          <el-radio-button label="Organism"></el-radio-button>
          <el-radio-button label="Class"></el-radio-button>
          <el-radio-button label="Provider"></el-radio-button>
        </el-radio-group>
        <span class="label do-not-print">and</span>
        <el-radio-group class="do-not-print" v-model="graphSection.timeScale" size="small" @change="getSummary">
          <el-radio-button label="Month"></el-radio-button>
          <el-radio-button label="Quarter"></el-radio-button>
          <el-radio-button label="Year"></el-radio-button>
        </el-radio-group>
      </template>
      <div style="margin: 10px">
        <stacked-column-chart printable :title="graphSection.title" :series="graphSection.series" :xAxisCategories="graphSection.xAxis" :yAxisTitle="graphSection.yAxis" :yAxisPlotLines="graphSection.plotLines" :usePerColumnLabels="true" :configuration="graphSection.customizations"></stacked-column-chart>
      </div>
    </page-section>

    <page-section class="do-not-print" title="Summary" collapsible collapsed v-loading="summarySection.loading">
      <template slot="title">
        <el-button data-testid="manage-resident-days" size="small" type="warning" @click="editDaysOfTherapy" class="dotButton do-not-print">Manage Resident Days</el-button>
        <el-button data-testid="export-summary" size="small" @click="exportSummaryToExcel" class="do-not-print">Export Data</el-button>
      </template>
      <el-table :data="summarySection.data">
        <el-table-column prop="label" :label="graphSection.groupBy"></el-table-column>
        <template>
          <el-table-column v-for="colKey in summarySection.columns" :key="colKey" :label="colKey">
            <template scope="scope">
              {{ scope.row[colKey] ? scope.row[colKey] : "--" }}
            </template>
          </el-table-column>
        </template>
        <el-table-column label="Total">
          <template scope="scope">
            {{ scope.row.summationColumn }}
          </template>
        </el-table-column>
      </el-table>
    </page-section>

    <page-section class="only-print" title="Summary">
      <TableGrid :columns="[{ key: 'label', label: graphSection.groupBy, width: 'max-content'}, ...summarySection.columns.map((key) => ({key, label: key, width: '1fr'})), { key: 'summationColumn', label: 'Total', width: '1fr'}]" :rows="summarySection.data" />
    </page-section>

    <page-section class="do-not-print" :title="`Analysis Details (${detailsSection.pagination.total})`" v-loading="!detailsTableReady">
      <template slot="title">
        <MedicationUsageReport :disabled="detailsSection.pagination.total === 0" :filters="global.filters" :reportRange="medicationUsageReportRange" />
        <el-button data-testid="export-observations" size="small" @click="exportObservationsToExcel()">Export Data</el-button>
        <el-pagination
          @size-change="
            size => {
              detailsSection.pagination.size = size;
            }
          "
          @current-change="
            page => {
              detailsSection.pagination.page = page;
            }
          "
          :page-sizes="[5, 10, 20, 100]"
          :page-size="detailsSection.pagination.size"
          layout="sizes, prev, pager, next"
          :total="detailsSection.pagination.total"
        >
        </el-pagination>
      </template>
      <el-table :data="detailsSection.data">
        <el-table-column label="Name" width="225" fixed>
          <template scope="scope">
            {{ scope.row.name }}
            <span class="clientId">({{ scope.row.client.staffId || scope.row.client.patientId }})</span>
          </template>
        </el-table-column>
        <el-table-column prop="facility" label="Facility" width="200" v-if="global.facilities.length > 1"></el-table-column>
        <el-table-column prop="client.location" label="Location" width="150"></el-table-column>
        <el-table-column prop="medication" label="Medication" width="200"> </el-table-column>
        <el-table-column prop="startDateFormatted" label="Start Date" width="100"> </el-table-column>
        <el-table-column prop="endDateFormatted" label="End Date" width="100"> </el-table-column>
        <el-table-column prop="daysOfTherapy" label="Rx DOT" width="75"> </el-table-column>
        <el-table-column prop="infectionCase.infectionSite" label="Infection Site" width="150"> </el-table-column>
        <el-table-column label="Status" width="75">
          <template slot-scope="scope">
            <img class="symptom-status-image" :src="statusImage(scope.row.infectionCase)" />
          </template>
        </el-table-column>
        <el-table-column label="Criteria Met?" width="125">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.criteriaMet === true" type="danger">Criteria Met</el-tag>
            <el-tag v-else-if="scope.row.criteriaMet === false" type="gray">Criteria Not Met</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="symptoms" label="Symptoms" width="275"> </el-table-column>
        <el-table-column prop="organism" label="Organism" width="150"> </el-table-column>
        <el-table-column prop="prophylactic" label="Prophylactic" width="100"> </el-table-column>
        <el-table-column prop="dosage" label="Dosage" width="75"> </el-table-column>
        <el-table-column prop="frequency" label="Frequency" width="100"> </el-table-column>
        <el-table-column prop="route" label="Route" width="100"> </el-table-column>
        <el-table-column prop="provider" label="Provider" min-width="200"> </el-table-column>
        <el-table-column prop="reviewedBy" label="Reviewed By" min-width="200"> </el-table-column>
        <el-table-column prop="reviewedDateFormatted" label="Reviewed" min-width="100"> </el-table-column>
      </el-table>
    </page-section>

    <DotEditor v-if="global.showDaysOfTherapy" v-on:hide="hideDaysOfTherapy" v-on:refresh="hideDaysOfTherapy"></DotEditor>
  </div>
</template>

<script>
import auth from "../../../auth";
import FiltersSection from "../../Shared/FiltersSection";
import PageSection from "../../Shared/PageSection";
import StackedColumnChart from "../../Shared/StackedColumnChart";
import util from "../../../util";
import moment from "moment";
import { downloadXlsx } from "../../../spreadsheet";
import DotEditor from "../ResidentDaysEditor";
import MedicationUsageReport from "../MedicationUsageReport.vue";
import TableGrid from "@/components/Shared/TableGrid.vue";

export default {
  name: "medication-usage-page",
  components: {
    "filters-section": FiltersSection,
    "page-section": PageSection,
    "stacked-column-chart": StackedColumnChart,
    DotEditor,
    MedicationUsageReport,
    TableGrid
  },
  watch: {
    "detailsSection.pagination.size"() {
      this.getDetails();
    },
    "detailsSection.pagination.page"() {
      this.getDetails();
    }
  },
  computed: {
    detailsTableReady() {
      return this.global.medications !== null && !this.detailsSection.loading;
    },
    medicationUsageReportRange() {
      return {
        filterDateStart: this.global.filters.prescriptionStartDateFrom,
        filterDateEnd: this.global.filters.prescriptionStartDateEnd,
      }
    }
  },
  data() {
    return {
      global: {
        showDaysOfTherapy: false,
        medications: null,
        facilities: [],
        filters: {},
        providers: {},
        defaultDateStart: moment()
          .subtract(90, "days")
          .toDate(),
        filterDefaults: { clientType: "Client", linkedTreatmentsOnly: true },
        additionalFilters: auth.userInfo().roles.indexOf("ROLE_ADMIN_STAFF") >= 0 ? ["outcomes", "primaryOrganisms", "clientType", "clientStatus", "firstName", "lastName"] : ["outcomes", "primaryOrganisms", "clientStatus", "firstName", "lastName"]
      },
      detailsSection: {
        loading: true,
        exportingObservations: false,
        observationExportProgress: 0,
        data: [],
        pagination: {
          page: 1,
          size: 10,
          total: 0
        }
      },
      graphSection: {
        loading: true,
        groupBy: "Medication",
        timeScale: "Month",
        groupOptions: {
          Medication: "medication",
          Class: "classification",
          Provider: "provider",
          "Infection Site": "infectionSite",
          Organism: "organism"
        },
        series: [],
        title: "Days of Therapy",
        yAxis: "Prescriptions",
        xAxis: [],
        plotLines: [],
        customizations: {
          legend: {
            enabled: true
          },
          plotOptions: {
            series: {
              dataLabels: {
                format: "{y}"
              }
            },
            column: {
              stacking: undefined
            }
          }
        }
      },
      summarySection: {
        loading: true,
        data: [],
        columns: [],
        showTrueCompliance: false,
        exportModels: {}
      }
    };
  },
  methods: {
    getFilterKeys() {
      return ["medicationDate", "medications", "linkedTreatmentsOnly", "providers", "reviewed", "basicInfectionSites", "basicInfectionSiteSubTypes", "caseStatus", "meetsCriteria"];
    },
    formatFilters(rawFilters) {
      const filterValues = Object.fromEntries(
        Object.entries(rawFilters)
          .filter(([k, v]) => v !== 0)
          .map(([k, v]) => {
            switch (k) {
              case "facilities":
                k = "facIds";
                break;
              case "linkedTreatmentsOnly":
                k = "linked";
                break;
              case "infectionSites":
                v = v[0] === 0 ? ["all"] : [v[0].type];
                break;
              case "resolutions":
                k = "outcomes";
                v = v[0] === 0 ? ["all"] : [v[0]];
                break;
              case "basicInfectionSites":
                k = "infectionSites";
                break;
              case "basicInfectionSiteSubTypes":
                k = "infectionSiteSubTypes";
                break;
              case "providers":
                v = v.map(id => `${this.global.providers[id].firstName} ${this.global.providers[id].lastName} (${this.global.providers[id].staffId})`);
                break;
            }
            return [k, v];
          })
      );
      this.global.filters = { ...filterValues };
      if (this.global.filters.reviewed === "all") {
        delete this.global.filters.reviewed;
      }
      this.global.filters.dateRangeStart = moment(filterValues.medicationDate[0])
        .startOf("day")
        .format();
      this.global.filters.dateRangeEnd = moment(filterValues.medicationDate[1])
        .endOf("day")
        .format();
      this.global.filters.prescriptionStartDateFrom = moment(filterValues.medicationDate[0])
        .startOf("day")
        .format();
      this.global.filters.prescriptionStartDateEnd = this.global.filters.dateRangeEnd;
      if (rawFilters.infectionSites && rawFilters.infectionSites[1]) this.global.filters.infectionSiteSubTypes = [rawFilters.infectionSites[1]];
      delete this.global.filters.medicationDate;
      this.refreshAllData();
    },
    statusImage(record) {
      const statusImageMap = {
        GI: "bug2",
        UTI: "bug3",
        Respiratory: "bug1",
        Skin: "bug4"
      };
      var result = "";
      var filename;
      if (record.symptomType) {
        filename = statusImageMap[record.symptomType];
        if (!record.status) {
          filename = filename + "s";
        }
        result = "/static/" + filename + ".png";
      }

      if (result === "") {
        filename = "bug4";
        if (!record.status) {
          filename = filename + "s";
        }
        result = "/static/" + filename + ".png";
      }

      return result;
    },
    async getDetails() {
      const json = await this.$http
        .post(`${window.CONFIG.treatement_api}/prescriptions/search`, {
          ...this.global.filters,
          includeInfectionCase: true,
          page: this.detailsSection.pagination.page - 1,
          size: this.detailsSection.pagination.size
        })
        .then(response => response.body)
        .catch(err => {
          if (err.status === 0) {
            this.detailsSection.loadingErrorDialog = true;
          }
          this.detailsSection.loading = false;
          return {
            page: 0,
            content: [],
            totalElements: 0
          };
        });
      this.detailsSection.pagination.total = json.totalElements;
      this.detailsSection.data = json.content.map(this.generateDetailsTableData);
      this.detailsSection.loading = false;
    },
    generateDetailsTableData(record) {
      let model = { ...record };
      model.startDateFormatted = moment(record.startDate).format(this.$configStore.dateFormat());
      model.endDateFormatted = moment(record.endDate).format(this.$configStore.dateFormat());
      model.medication = this.global.medications[model.medicationId]?.displayText;
      model.classification = this.global.medications[model.medicationId]?.classification;
      model.prophylactic = model.prophylactic ? "Yes" : "No";
      model.facility = this.global.facilities.filter(s => s.id === Number(record.facId))[0].displayText;
      model.reviewedDateFormatted = record.reviewed ? moment(record.reviewed).format(this.$configStore.dateFormat()) : "";

      const { firstName, lastName, middleName } = model.client;
      model.name = [firstName, middleName, lastName].filter(Boolean).join(" ");
      model.criteriaMet = model.infectionCase.hasRulesets && model.infectionCase.lastRulesRunDate ? !!model.infectionCase?.ruleIds?.length : null;
      model.symptoms = model.infectionCase.symptoms.map(({ displayText }) => displayText).join(", ");

      return model;
    },
    async getSummary() {
      this.graphSection.loading = true;
      this.summarySection.loading = true;
      const summary = await this.$http
        .post(`${window.CONFIG.treatement_api}/prescriptions/days-of-therapy/summary`, {
          ...this.global.filters,
          groupBy: this.graphSection.groupOptions[this.graphSection.groupBy],
          timeScale: this.graphSection.timeScale.toLowerCase(),
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone
        })
        .then(res => res.body)
        .catch(() => {
          this.summarySection.loading = false;
          this.graphSection.loading = false;
          return [];
        });
      const [groups, periods] = this.createSummary(summary);
      this.summarySection.exportModels = { groups, periods };
      this.generateSummaryTableData(groups, periods);
      this.generateGraphData(groups, periods);
      this.graphSection.loading = false;
      this.summarySection.loading = false;
    },
    generateGraphData(groupedTotals, timePeriods) {
      const orderedTotals = [...groupedTotals.entries()];
      this.graphSection.xAxis = timePeriods;
      this.graphSection.series = orderedTotals.flatMap(([name, periods]) => {
        return {
          name,
          data: timePeriods.map(period => periods[period]),
          dataLabels: {
            enabled: true
          }
        };
      });
    },
    roundTwoDecimalPlaces(value) {
      return Math.round(value * 100) / 100;
    },
    generateSummaryTableData(groupedTotals, timePeriods) {
      this.summarySection.data = [];
      this.summarySection.columns = [...timePeriods];
      const orderedTotals = [...groupedTotals.entries()];
      const summationRow = { label: "Total", summationColumn: 0 };
      orderedTotals.forEach(([groupLabel, value]) => {
        const row = { label: groupLabel, ...value };
        this.summarySection.columns.forEach(colKey => {
          row.summationColumn = row.summationColumn || 0;
          row.summationColumn += row[colKey];
          row.summationColumn = this.roundTwoDecimalPlaces(row.summationColumn);

          // sum up a column's count value
          summationRow[colKey] = summationRow[colKey] || 0;
          summationRow[colKey] += row[colKey];
          summationRow[colKey] = this.roundTwoDecimalPlaces(summationRow[colKey]);
          summationRow.summationColumn += row[colKey];
          summationRow.summationColumn = this.roundTwoDecimalPlaces(summationRow.summationColumn);
        });
        this.summarySection.data.push(row);
      });
      this.summarySection.data.push(summationRow);
    },
    createSummary(summary) {
      const timeScale = this.graphSection.timeScale;
      const formatTime = {
        Week: record => `W${record.dategroup}-${record.year}`,
        Month: record => `${record.year}-${`${record.dategroup}`.padStart(2, "0")}`,
        Quarter: record => `Q${record.dategroup}-${record.year}`,
        Year: record => `${record.year}`
      };

      const allTimePeriods = new Map();
      const groupKey = this.graphSection.groupOptions[this.graphSection.groupBy];

      const convertAndGroup = record => {
        const row = {
          time: formatTime[timeScale](record),
          group: record[groupKey],
          count: record.total,
          data: record
        };
        allTimePeriods.set(row.time, 0);
        return row;
      };

      const orderedCounts = summary.map(convertAndGroup);
      const getSummaryDefaults = () => JSON.parse(JSON.stringify(Object.fromEntries(allTimePeriods)));

      const groupedTotals = new Map();
      orderedCounts.forEach(row => {
        if (!groupedTotals.has(row.group)) {
          groupedTotals.set(row.group, getSummaryDefaults());
        }
        const values = groupedTotals.get(row.group);
        values[row.time] = values[row.time] || 0;
        values[row.time] += row.count;
        groupedTotals.set(row.group, values);
      });
      return [groupedTotals, [...new Set(orderedCounts.map(c => c.time))]];
    },
    async autofillResidentDays() {
      const params = {
        dateRangeStart: this.global.filters.dateRangeStart,
        dateRangeEnd: this.global.filters.dateRangeEnd,
        ...(this.global.filters.facIds ? { facIds: this.global.filters.facIds } : {}),
        ...(this.global.filters.unitIds ? { unitIds: this.global.filters.unitIds } : {}),
        autofillResidentDays: true
      };
      return this.$http.post(`${window.CONFIG.treatement_api}/resident-days/autofill`, params);
    },
    async refreshAllData() {
      this.graphSection.loading = true;
      this.summarySection.loading = true;
      if (this.detailsTableReady) {
        this.getDetails();
      }
      await this.autofillResidentDays();
      this.getSummary();
    },
    async exportObservationsToExcel() {
      this.detailsSection.exportingObservations = true;
      this.detailsSection.observationExportProgress = 0;
      let pageProgress = 0;
      let hasMorePages = true;
      const size = 1000;
      const columns = ["Individual ID", "First Name", "Last Name", "Facility", "Location", "Medication", "Class", "Start Date", "End Date", "Prescription DOT", "Infection Site", "Status", "Criteria Met", "Symptoms", "Organism", "Prophylactic", "Dosage", "Frequency", "Route", "Provider", "Reviewed By", "Reviewed Date"];

      const convertDataToColumns = json => {
        const data = this.generateDetailsTableData(json);
        const { facility, client, medication, classification, prophylactic, startDateFormatted, endDateFormatted, criteriaMet, symptoms, organism, daysOfTherapy, dosage, frequency, route, provider, infectionCase, reviewedBy, reviewedDateFormatted } = data;
        const { staffId, patientId, firstName, lastName, location } = client;
        const { status, infectionSite } = infectionCase;
        return [staffId ?? patientId, firstName, lastName, facility, location, medication, classification, startDateFormatted, endDateFormatted, daysOfTherapy, infectionSite, status ? "Confirmed" : "Suspected", criteriaMet === true ? "Criteria Met" : criteriaMet === false ? "Criteria Not Met" : "", symptoms, organism, prophylactic, dosage, frequency, route, provider, reviewedBy, reviewedDateFormatted];
      };

      const getPageOfData = async page => {
        return await this.$http
          .post(`${window.CONFIG.treatement_api}/prescriptions/search`, {
            ...this.global.filters,
            includeInfectionCase: true,
            page,
            size
          })
          .then(response => response.body);
      };
      let data = [];
      while (hasMorePages) {
        this.detailsSection.exportingErrorDialog = false;
        if (!this.detailsSection.exportingObservations) {
          this.cancelObservationExport();
          break;
        }
        const json = await getPageOfData(pageProgress + 1).catch(err => {
          if (err.status === 0) {
            this.cancelObservationExport();
            this.detailsSection.exportingErrorDialog = true;
          }
        });
        this.detailsSection.observationExportProgress = Math.round(((pageProgress + 1) / json.totalPages) * 100);
        if (json.totalPages > pageProgress + 1) {
          pageProgress++;
        } else {
          hasMorePages = false;
        }
        data.push(json.content);
      }
      if (!this.detailsSection.exportingObservations) {
        return;
      }
      data = data.flat().map(convertDataToColumns);
      await this.createExcelFile(`medication-records-${moment().format("YYYY-MM-DD")}.xlsx`, columns, data);
      this.detailsSection.exportingObservations = false;
      this.detailsSection.exportingErrorDialog = false;
    },
    createExcelFile: function(fileName, columns, data) {
      const detailsRow = util.generateExportDetailsRow();
      data.unshift(columns);
      data.unshift(detailsRow);
      return downloadXlsx(fileName, [{ sheet: "Sheet1", rows: data }]);
    },
    async exportSummaryToExcel() {
      this.summarySection.loading = true;
      const columns = [this.graphSection.groupBy, ...this.summarySection.columns, "Total"];
      const data = this.summarySection.data.map(row => {
        return [row.label, ...this.summarySection.columns.map(period => row[period].count), row.summationColumn];
      });
      await this.createExcelFile(`medication-records-${moment().format("YYYY-MM-DD")}.xlsx`, columns, data);
      this.summarySection.loading = false;
    },
    editDaysOfTherapy() {
      this.global.showDaysOfTherapy = true;
    },
    hideDaysOfTherapy() {
      this.global.showDaysOfTherapy = false;
    }
  },
  async created() {
    console.clear();
    this.global.facilities = this.$configStore.data.sites;
    this.$http
      .get(`${window.CONFIG.legacy_facade_api}/clients/search`, {
        params: {
          providerType: "Yes",
          status: "C",
          clientType: "Staff"
        }
      })
      .then(resp => resp.json())
      .then(providers => {
        this.global.providers = util.arrayToObj(providers, "id");
      });
    await this.$http
      .get(`${window.CONFIG.treatement_api}/config`)
      .then(resp => resp.json())
      .then(json => [...json.medications, ...json.archivedMedications])
      .then(meds => {
        this.global.medications = util.arrayToObj(meds, "id");
        this.getDetails();
      });
  }
};
</script>
<style scoped>
.clientId {
  color: #a7a7a7;
}
</style>
